(() => {
  class ProductMediaGallery {
    constructor(element) {
      this.element = element;
      this.main = element.querySelector('[data-product-media-main]');
      this.thumbnails = Array.from(element.querySelectorAll('[data-product-media-thumbnail]'));
      this.lightbox = element.querySelector('[data-product-media-lightbox]');
      this.lightboxContent = element.querySelector('[data-product-media-lightbox-content]');
      this.lightboxClose = element.querySelector('[data-product-media-lightbox-close]');
      this.selectedMediaId = this.main && this.main.dataset.selectedMediaId;
      this.renderedMediaId = this.selectedMediaId;
      this.deferredModelPoster = this.main?.querySelector('[data-product-media-activate]')?.cloneNode(true);
      this.deferredModelId = this.deferredModelPoster ? this.selectedMediaId : null;
      this.renderedMediaMode = this.deferredModelPoster ? 'preview' : 'full';
      this.modelActivated = false;

      if (!this.main || !this.selectedMediaId) return;

      this.onVariantChange = this.onVariantChange.bind(this);
      this.bindEvents();
    }

    bindEvents() {
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('mouseenter', () => this.previewThumbnail(thumbnail));
        thumbnail.addEventListener('mouseleave', () => this.renderSelected());
        thumbnail.addEventListener('focus', () => this.previewThumbnail(thumbnail));
        thumbnail.addEventListener('blur', () => this.renderSelected());
        thumbnail.addEventListener('click', () => this.selectThumbnail(thumbnail));
      });

      this.main.addEventListener('mousemove', (event) => this.updateZoom(event));
      this.main.addEventListener('mouseleave', () => this.resetZoom());
      this.main.addEventListener('click', (event) => {
        if (event.target.closest('[data-product-media-activate]')) {
          this.modelActivated = true;
          this.renderSelected();
          const model = this.main.querySelector('model-viewer');
          if (model) {
            model.tabIndex = 0;
            model.focus();
          }
        } else {
          this.openLightbox(event);
        }
      });

      if (this.lightboxClose) {
        this.lightboxClose.addEventListener('click', () => this.closeLightbox());
      }

      if (this.lightbox) {
        this.lightbox.addEventListener('click', (event) => {
          const clickedMedia = event.target.closest('img, video, iframe, model-viewer');
          const clickedClose = event.target.closest('[data-product-media-lightbox-close]');

          if (!clickedMedia && !clickedClose) this.closeLightbox();
        });
      }

      document.addEventListener('product:variant-change', this.onVariantChange);
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(this.element)) return;
        document.removeEventListener('product:variant-change', this.onVariantChange);
      });
    }

    getTemplate(mediaId, mode = 'full') {
      const selector = mode === 'preview'
        ? `[data-product-media-preview-template="${mediaId}"]`
        : `[data-product-media-template="${mediaId}"]`;

      return this.element.querySelector(selector);
    }

    renderMedia(mediaId, mode = 'full') {
      if (this.renderedMediaId === mediaId && this.renderedMediaMode === mode) return;

      const template = this.getTemplate(mediaId, mode);

      if (!template) return;

      this.main.replaceChildren(template.content.cloneNode(true));
      this.renderedMediaId = mediaId;
      this.renderedMediaMode = mode;
    }

    renderSelected() {
      // Leaving a thumbnail must restore the poster without starting the model.
      if (this.selectedMediaId === this.deferredModelId && !this.modelActivated) {
        if (this.renderedMediaId !== this.selectedMediaId || this.renderedMediaMode !== 'preview') {
          this.main.replaceChildren(this.deferredModelPoster.cloneNode(true));
          this.renderedMediaId = this.selectedMediaId;
          this.renderedMediaMode = 'preview';
        }
        return;
      }
      this.renderMedia(this.selectedMediaId, 'full');
    }

    previewThumbnail(thumbnail) {
      // Native players can change the frame height. Wait for selection so a
      // hover cannot move the thumbnail before the shopper clicks it.
      if (thumbnail.dataset.mediaType !== 'image') return;
      const mediaId = thumbnail.dataset.mediaId;
      if (mediaId === this.selectedMediaId && this.renderedMediaMode === 'full') return;
      this.renderMedia(mediaId, 'full');
    }

    selectThumbnail(thumbnail) {
      this.selectedMediaId = thumbnail.dataset.mediaId;
      if (this.selectedMediaId === this.deferredModelId) this.modelActivated = true;
      this.main.dataset.selectedMediaId = this.selectedMediaId;
      this.updateSelectedThumbnail();
      this.renderSelected();
    }

    updateSelectedThumbnail() {
      this.thumbnails.forEach((thumbnail) => {
        const isSelected = thumbnail.dataset.mediaId === this.selectedMediaId;

        thumbnail.classList.toggle('is-selected', isSelected);
        thumbnail.setAttribute('aria-pressed', String(isSelected));
      });
    }

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      const mediaId = variant && variant.featured_media_id;
      const eventScope = event.detail && event.detail.scope;
      const scope = this.element.closest('.product-layout, .featured-product-showcase, [data-search-product-drawer]') || '';

      if (!mediaId) return;
      if ((eventScope || scope) && eventScope !== scope) return;

      const thumbnail = this.thumbnails.find((item) => item.dataset.mediaId === String(mediaId));

      if (!thumbnail) return;

      this.selectThumbnail(thumbnail);
      thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    updateZoom(event) {
      const frame = event.target.closest('[data-product-media-zoom]');

      if (!frame || !this.main.contains(frame)) return;

      const rect = frame.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      frame.style.setProperty('--product-media-zoom-x', `${x}%`);
      frame.style.setProperty('--product-media-zoom-y', `${y}%`);
    }

    resetZoom() {
      const frame = this.main.querySelector('[data-product-media-zoom]');

      if (!frame) return;

      frame.style.removeProperty('--product-media-zoom-x');
      frame.style.removeProperty('--product-media-zoom-y');
    }

    openLightbox(event) {
      const trigger = event.target.closest('[data-product-media-lightbox-trigger]');

      if (!trigger || !this.main.contains(trigger) || !this.lightbox || !this.lightboxContent) return;

      const image = trigger.querySelector('img');

      if (!image) return;

      const lightboxImage = image.cloneNode();

      lightboxImage.removeAttribute('sizes');
      lightboxImage.removeAttribute('srcset');
      this.lightboxContent.replaceChildren(lightboxImage);
      this.lightbox.showModal();
    }

    closeLightbox() {
      if (!this.lightbox || !this.lightbox.open) return;

      this.lightbox.close();
      this.lightboxContent.replaceChildren();
    }
  }

  const initGalleries = (scope = document) => {
    scope.querySelectorAll('[data-product-media-gallery]').forEach((element) => {
      if (element.dataset.productMediaGalleryInitialized === 'true') return;

      element.dataset.productMediaGalleryInitialized = 'true';
      new ProductMediaGallery(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initGalleries());
  } else {
    initGalleries();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initGalleries(event.target);
  });
})();
